import { Dispatch, Fragment, SetStateAction } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import { UserField } from '~/types/user'
import { UserSkill } from '~/types/user-info-types'
import { getBirthYearOptions, gradeLevelData, yearsInSchoolData } from '~/shared/utils/userInfo.utils'
import { getTimezones } from '~/utils/timezone'
import { getUserSkillLabel } from '~/utils'
import { AppLanguage } from '~/types/locale'
import SelectOptionCountries from '~/components/PanelSettingsForm/SelectOptionCountries'

export default function SelectOptions({
  fieldName,
  options,
  placeHolder,
  setHasCountriesLoaded,
}: {
  fieldName?: UserField
  options?: Array<{ value: string; label: string }>
  placeHolder?: string
  setHasCountriesLoaded?: Dispatch<SetStateAction<boolean>>
}): React.ReactElement {
  const router = useRouter()

  const { t } = useTranslation(['registerAccount', 'common'])
  const language = router.locale as AppLanguage

  if (options) {
    return (
      <Fragment>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Fragment>
    )
  }

  switch (fieldName) {
    case UserField.TIMEZONE:
      return (
        <Fragment>
          <option value="">{t('registerAccount:timezone')}</option>
          {getTimezones().map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Fragment>
      )

    case UserField.BIRTH_YEAR:
      return (
        <Fragment>
          <option value="">{placeHolder ? placeHolder : t('registerAccount:indBirth')}</option>
          {getBirthYearOptions().map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Fragment>
      )

    case UserField.COUNTRY:
      return (
        <Fragment>
          <option value="">{t('registerAccount:country')}</option>
          <SelectOptionCountries setHasCountriesLoaded={setHasCountriesLoaded} />
        </Fragment>
      )

    case UserField.SKILL:
      return (
        <Fragment>
          <option value="">{t('registerAccount:indLevel')}</option>
          {Object.values(UserSkill).map((option) => {
            if (typeof option !== 'number') {
              return null
            }

            return (
              <option key={option} value={option}>
                {getUserSkillLabel(option, t)}
              </option>
            )
          })}
        </Fragment>
      )

    case UserField.YEARS_IN_SCHOOL:
      return (
        <Fragment>
          <option value="">{t('registerAccount:dropdownYearsInSchool')}</option>
          {yearsInSchoolData.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Fragment>
      )

    case UserField.GRADE_LEVEL_ID:
      return (
        <Fragment>
          <option value="">{t('registerAccount:gradeLevel')}</option>
          {gradeLevelData(language).map((option) => (
            <option key={option} value={option}>
              {t(`common:gradeLevels.${option.toString()}`)}
            </option>
          ))}
        </Fragment>
      )

    default:
      return <Fragment />
  }
}
